import React, { useState } from 'react';
import { Modal, Box, Button } from '@mui/material';
import { FaSearchPlus, FaSearchMinus, FaChevronLeft, FaChevronRight, FaDownload } from 'react-icons/fa';
import { IEvidence } from '../../../../interfaces/ITask';

interface MediaViewerProps {
  isOpen: boolean;
  onClose: () => void;
  media: IEvidence | null;
  allMedia: IEvidence[];
  currentIndex: number;
  onNavigate: (direction: 'prev' | 'next') => void;
}

const MediaViewer: React.FC<MediaViewerProps> = ({
  isOpen,
  onClose,
  media,
  allMedia,
  currentIndex,
  onNavigate,
}) => {
  const [isZoomed, setIsZoomed] = useState(false);

  if (!media) return null;

  const toggleZoom = () => setIsZoomed(!isZoomed);

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = media.link;
    link.download = media.fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getFileType = (fileName: string): 'image' | 'video' | 'pdf' | 'other' => {
    const ext = fileName.toLowerCase().split('.').pop();
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext || '')) return 'image';
    if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm'].includes(ext || '')) return 'video';
    if (ext === 'pdf') return 'pdf';
    return 'other';
  };

  const fileType = getFileType(media.fileName);

  const renderMedia = () => {
    switch (fileType) {
      case 'image':
        return (
          <img
            src={media.link}
            alt={media.fileName}
            style={{
              maxWidth: isZoomed ? 'none' : '80vw',
              maxHeight: isZoomed ? 'none' : '80vh',
              objectFit: isZoomed ? 'none' : 'contain',
              borderRadius: 8,
              boxShadow: '0 0 24px 2px #000',
              background: '#222',
              display: 'block',
              margin: 'auto',
              cursor: isZoomed ? 'zoom-out' : 'zoom-in',
            }}
            onClick={toggleZoom}
          />
        );
      
      case 'video':
        return (
          <video
            src={media.link}
            controls
            style={{
              maxWidth: '80vw',
              maxHeight: '80vh',
              borderRadius: 8,
              boxShadow: '0 0 24px 2px #000',
              background: '#222',
              display: 'block',
              margin: 'auto',
            }}
          />
        );
      
      case 'pdf':
        return (
          <iframe
            src={media.link}
            title={media.fileName}
            style={{
              width: '80vw',
              height: '80vh',
              border: 'none',
              borderRadius: 8,
              boxShadow: '0 0 24px 2px #000',
              background: 'white',
            }}
          />
        );
      
      default:
        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '40px',
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: 8,
              color: 'white',
              minHeight: '200px',
            }}
          >
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>📄</div>
            <h3>Arquivo não pode ser visualizado</h3>
            <p>{media.fileName}</p>
            <Button
              onClick={handleDownload}
              variant="contained"
              startIcon={<FaDownload />}
              style={{ marginTop: '20px' }}
            >
              Baixar Arquivo
            </Button>
          </div>
        );
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.92)',
        zIndex: 2000,
      }}
      BackdropProps={{ style: { backgroundColor: 'rgba(0,0,0,0.92)' } }}
    >
      <Box
        sx={{
          position: 'relative',
          maxWidth: '95vw',
          maxHeight: '95vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: 'auto',
          boxShadow: 6,
          borderRadius: 2,
          background: 'rgba(0,0,0,0.0)',
          overflow: isZoomed ? 'auto' : 'hidden',
        }}
      >
        {/* Botão de fechar (X) */}
        <Button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: 10,
            right: 10,
            zIndex: 3000,
            backgroundColor: 'rgba(0,0,0,0.8)',
            color: 'white',
            border: '2px solid #fff',
            borderRadius: '50%',
            width: 40,
            height: 40,
            minWidth: 40,
            minHeight: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 22,
            boxShadow: '0 2px 8px #000',
            cursor: 'pointer',
          }}
        >
          ×
        </Button>

        {/* Botão de download */}
        <Button
          onClick={handleDownload}
          style={{
            position: 'absolute',
            top: 10,
            right: 60,
            zIndex: 3000,
            backgroundColor: 'rgba(0,0,0,0.8)',
            color: 'white',
            border: '2px solid #fff',
            borderRadius: '50%',
            width: 40,
            height: 40,
            minWidth: 40,
            minHeight: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 16,
            boxShadow: '0 2px 8px #000',
            cursor: 'pointer',
          }}
          title="Baixar arquivo"
        >
          <FaDownload />
        </Button>

        {/* Botão de zoom (apenas para imagens) */}
        {fileType === 'image' && (
          <Button
            onClick={toggleZoom}
            style={{
              position: 'absolute',
              bottom: 20,
              right: 60,
              zIndex: 3000,
              backgroundColor: 'rgba(0,0,0,0.8)',
              color: 'white',
              border: '2px solid #fff',
              borderRadius: '50%',
              width: 40,
              height: 40,
              minWidth: 40,
              minHeight: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 16,
              boxShadow: '0 2px 8px #000',
              cursor: 'pointer',
            }}
            title={isZoomed ? 'Reduzir' : 'Zoom'}
          >
            {isZoomed ? <FaSearchMinus /> : <FaSearchPlus />}
          </Button>
        )}

        {/* Navegação (apenas se houver mais de um arquivo) */}
        {allMedia.length > 1 && (
          <>
            {/* Seta esquerda */}
            <Button
              onClick={() => onNavigate('prev')}
              style={{
                position: 'absolute',
                left: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'rgba(0, 0, 0, 0.85)',
                color: 'white',
                border: 'none',
                zIndex: 2500,
                fontSize: 48,
                borderRadius: '0 24px 24px 0',
                minWidth: 56,
                minHeight: 80,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 8px #000',
                cursor: 'pointer',
                opacity: 0.8,
              }}
            >
              <FaChevronLeft />
            </Button>

            {/* Seta direita */}
            <Button
              onClick={() => onNavigate('next')}
              style={{
                position: 'absolute',
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'rgba(0, 0, 0, 0.85)',
                color: 'white',
                border: 'none',
                zIndex: 2500,
                fontSize: 48,
                borderRadius: '24px 0 0 24px',
                minWidth: 56,
                minHeight: 80,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 8px #000',
                cursor: 'pointer',
                opacity: 0.8,
              }}
            >
              <FaChevronRight />
            </Button>
          </>
        )}

        {/* Conteúdo da mídia */}
        {renderMedia()}

        {/* Info do arquivo */}
        <div
          style={{
            position: 'absolute',
            bottom: 20,
            left: 20,
            background: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            padding: '10px 15px',
            borderRadius: '8px',
            fontSize: '14px',
            zIndex: 3000,
            border: '1px solid #444',
            maxWidth: '60%',
          }}
        >
          <div style={{ fontWeight: 'bold' }}>
            {currentIndex + 1} de {allMedia.length}
          </div>
          <div style={{ 
            wordBreak: 'break-word',
            marginTop: '4px',
            fontSize: '12px',
            opacity: 0.9 
          }}>
            {media.fileName}
          </div>
        </div>
      </Box>
    </Modal>
  );
};

export default MediaViewer; 